import { useEffect, useRef } from "react";
import { defaultTeacherGrade } from "utils/standards";

/**
 * Default a grade selector to the teacher's own grade, exactly once, on first
 * open — so it never fights a later manual change. Shared by every "create"
 * flow with a grade dropdown (lessons/standards standard mode, quick group).
 *
 * Calls `apply(grade)` with the teacher's lowest grade_levels entry when:
 *   - the account has loaded, AND
 *   - the teacher has a recognizable grade, AND
 *   - (when `availableGrades` is given) that grade is one of the options.
 * No-ops (leaving the caller's existing default) when the teacher has no grade.
 *
 * @param {string[]} gradeLevels  teacher grade_levels from the account
 * @param {boolean}  loaded       account fetch settled
 * @param {string[]|undefined} availableGrades  dropdown options; omit to apply
 *                                as soon as loaded (the option is assumed valid)
 * @param {(grade: string) => void} apply  stable setter (e.g. setSelectedGrade)
 */
export const useTeacherGradeDefault = (gradeLevels, loaded, availableGrades, apply) => {
  const applied = useRef(false);
  useEffect(() => {
    if (applied.current || !loaded) return;
    const grade = defaultTeacherGrade(gradeLevels);
    if (!grade) {
      applied.current = true; // no teacher grade → keep the caller's default, once
      return;
    }
    if (availableGrades && !availableGrades.includes(grade)) return; // wait for options
    applied.current = true;
    apply(grade);
  }, [gradeLevels, loaded, availableGrades, apply]);
};
